import * as chrono from 'chrono-node';

/**
 * Aus einem diktierten Satz werden Titel, Zeitpunkt und Wiederholung
 * (docs/KONZEPT-ERINNERUNGEN.md, Abschnitt 4.2). Alles davon passiert lokal.
 *
 * `chrono-node` bringt ein deutsches Locale mit und trifft Datumsangaben zuverlässig,
 * scheitert aber genau an gesprochenem Deutsch: „morgen früh um 8" wird 9:00, „halb
 * neun" wird 9:00, „jeden Montag" verliert die Wiederholung. Die Schicht hier räumt
 * das ab, bevor chrono den Satz sieht.
 */

export type RepeatUnit = 'day' | 'week' | 'month' | 'year';

/**
 * Eine Wiederholung ist ein Abstand und eine Einheit — „alle zwei Wochen" ist damit
 * dasselbe Bauteil wie „wöchentlich" und nicht ein Sonderfall daneben.
 *
 * Die vier alten Zeichenketten bleiben lesbar: In bestehenden Vaults stehen sie in
 * den Erinnerungen, und ein Datenmodell, das seine eigene Vergangenheit nicht mehr
 * versteht, verliert Einträge.
 */
export type RepeatRule = {
  unit: RepeatUnit;
  /** Abstand in Einheiten; 1 ist der Normalfall. */
  every: number;
  /** Fester Wochentag (0 = Sonntag), wenn einer genannt wurde. */
  weekday?: number;
  /**
   * Der wievielte dieser Wochentage im Monat — 1 bis 4, oder -1 für den letzten.
   * Nur zusammen mit `unit: 'month'`: „jeden ersten Montag im Monat".
   */
  nth?: number;
};

export type Repeat = RepeatRule | 'daily' | 'weekly' | 'monthly' | 'yearly';

const LEGACY: Record<string, RepeatRule> = {
  daily: { unit: 'day', every: 1 },
  weekly: { unit: 'week', every: 1 },
  monthly: { unit: 'month', every: 1 },
  yearly: { unit: 'year', every: 1 },
};

/** Bringt beide Schreibweisen auf dieselbe Form. */
export function toRule(repeat: Repeat): RepeatRule {
  if (typeof repeat === 'string') return LEGACY[repeat] ?? LEGACY.weekly!;
  const every = Math.min(99, Math.max(1, Math.round(repeat.every || 1)));
  return { ...repeat, every };
}

/**
 * Der n-te Wochentag eines Monats, -1 für den letzten. Gibt es keinen fünften
 * Dienstag, wird der letzte genommen — sonst fiele der Termin in manchen Monaten aus.
 */
function nthWeekday(year: number, month: number, weekday: number, nth: number): Date {
  if (nth < 0) {
    const last = new Date(Date.UTC(year, month + 1, 0));
    last.setUTCDate(last.getUTCDate() - ((last.getUTCDay() - weekday + 7) % 7));
    return last;
  }
  const first = new Date(Date.UTC(year, month, 1));
  const offset = (weekday - first.getUTCDay() + 7) % 7;
  const day = 1 + offset + (nth - 1) * 7;
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, day > lastDay ? day - 7 : day));
}

const TAGE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const TAGS = ['sonntags', 'montags', 'dienstags', 'mittwochs', 'donnerstags', 'freitags', 'samstags'];

/** Beschriftung für die Liste. */
export function repeatLabel(repeat: Repeat): string {
  const rule = toRule(repeat);
  const day = rule.weekday != null ? TAGE[rule.weekday] : null;
  if (rule.unit === 'month' && day && rule.nth) {
    const which = rule.nth < 0 ? 'letzten' : `${rule.nth}.`;
    return `jeden ${which} ${day} im Monat`;
  }
  if (rule.every === 1) {
    if (rule.unit === 'day') return 'täglich';
    if (rule.unit === 'week') return day ? `jeden ${day}` : 'wöchentlich';
    if (rule.unit === 'month') return 'monatlich';
    return 'jährlich';
  }
  if (rule.unit === 'day') return `alle ${rule.every} Tage`;
  if (rule.unit === 'week') {
    // Nicht „jeden 2. Dienstag" — das liest sich wie der zweite Dienstag im Monat.
    return day ? `alle ${rule.every} Wochen ${TAGS[rule.weekday!]}` : `alle ${rule.every} Wochen`;
  }
  if (rule.unit === 'month') return `alle ${rule.every} Monate`;
  return `alle ${rule.every} Jahre`;
}

export type Parsed = {
  title: string;
  dueAt: number | null;
  repeat: Repeat | null;
  /** Der von chrono erkannte Wortlaut — für die Marke über dem Eintrag. */
  matched: string | null;
};

const ZAHL: Record<string, number> = {
  ein: 1,
  eins: 1,
  zwei: 2,
  drei: 3,
  vier: 4,
  fünf: 5,
  fuenf: 5,
  sechs: 6,
  sieben: 7,
  acht: 8,
  neun: 9,
  zehn: 10,
  elf: 11,
  zwölf: 12,
  zwoelf: 12,
};

const EINHEIT: Record<string, RepeatUnit> = {
  tag: 'day',
  tage: 'day',
  tagen: 'day',
  woche: 'week',
  wochen: 'week',
  monat: 'month',
  monate: 'month',
  monaten: 'month',
  jahr: 'year',
  jahre: 'year',
  jahren: 'year',
};

const WOCHENTAGE = 'montag|dienstag|mittwoch|donnerstag|freitag|samstag|sonntag';

/** „jeden zweiten Dienstag" — die Ordnungszahl trägt den Abstand. */
const ORDNUNG: Record<string, number> = {
  letzten: -1, letzte: -1,
  ersten: 1, erste: 1, zweiten: 2, zweite: 2, zweiter: 2, dritten: 3, dritte: 3,
  vierten: 4, vierte: 4, fünften: 5, fuenften: 5, sechsten: 6, siebten: 7,
  siebenten: 7, achten: 8, neunten: 9, zehnten: 10, elften: 11, zwölften: 12,
  zwoelften: 12,
};

/** Stunde als Ziffer, egal ob „neun" oder „9" gesprochen wurde. */
function stunde(word: string): number {
  return ZAHL[word.toLowerCase()] ?? Number(word);
}

/** Volle Stunde vor der genannten — „halb neun" ist 8:30, nicht 9:30. */
function davor(hour: number): number {
  return (hour + 11) % 12 || 12;
}

/** Ordnungszahl als Wort oder als Ziffer mit Punkt. */
function ordnung(word: string | undefined): number {
  if (!word) return 1;
  const clean = word.toLowerCase().replace(/\.$/, '');
  return ORDNUNG[clean] ?? (Number(clean) || 1);
}

/**
 * Zieht die Wiederholung heraus und entfernt das „jeden" aus dem Text. Ein Wochentag
 * bleibt stehen, damit chrono daraus noch den ersten Termin bildet.
 *
 * Zwei Formen: „jeden [zweiten] <Einheit|Wochentag>" und „alle zwei Wochen".
 */
function extractRepeat(text: string): { text: string; repeat: Repeat | null } {
  const ordinals = `${Object.keys(ORDNUNG).join('|')}|\\d{1,2}\\.`;
  const jeden = new RegExp(
    `\\bjede[nrs]?\\s+(?:(${ordinals})\\s+)?(tag|woche|monat|jahr|${WOCHENTAGE})\\b`,
    'i',
  );
  const alle = new RegExp(
    `\\balle\\s+(${Object.keys(ZAHL).join('|')}|\\d{1,3})\\s+(tage|tagen|wochen|monate|monaten|jahre|jahren)\\b`,
    'i',
  );

  const match = jeden.exec(text);
  if (match) {
    const word = match[2]!.toLowerCase();
    const index = WOCHENTAGE.split('|').indexOf(word);
    // Ein Wochentag ohne eigene Einheit bedeutet wöchentlich; „jeden Tag" ist täglich.
    const unit = EINHEIT[word] ?? 'week';
    const count = ordnung(match[1]);
    // 0 ist Sonntag, die Liste beginnt bei Montag.
    const weekday = index >= 0 ? (index + 1) % 7 : undefined;

    // „jeden zweiten Dienstag" heißt umgangssprachlich alle vierzehn Tage. „jeden
    // ersten Montag" kann das nicht heißen — das ist der Monatsrhythmus, ebenso
    // „jeden letzten Freitag" und alles mit ausdrücklichem „im Monat".
    // Ohne Ordnungszahl ist „jeden Montag" schlicht wöchentlich.
    const monthly =
      weekday !== undefined &&
      match[1] !== undefined &&
      (count <= 1 || /\bim\s+monat\b/i.test(text));
    let rest = text.replace(match[0], EINHEIT[word] ? '' : match[2]!);
    const rule: RepeatRule = monthly
      ? { unit: 'month', every: 1, weekday, nth: count }
      : { unit, every: Math.abs(count), ...(weekday !== undefined ? { weekday } : {}) };
    if (monthly) rest = rest.replace(/\bim\s+monat\b/i, ' ');
    return { text: rest, repeat: rule };
  }

  const every = alle.exec(text);
  if (every) {
    const count = ZAHL[every[1]!.toLowerCase()] ?? Number(every[1]);
    return {
      text: text.replace(every[0], ''),
      repeat: { unit: EINHEIT[every[2]!.toLowerCase()]!, every: count || 1 },
    };
  }

  return { text, repeat: null };
}

/** Umgangssprachliche Uhrzeiten in etwas übersetzen, das chrono versteht. */
function normaliseTime(text: string): string {
  return (
    text
      .replace(/\bviertel\s+vor\s+(\w+)/gi, (_, word: string) => `${davor(stunde(word))}:45`)
      .replace(/\bviertel\s+nach\s+(\w+)/gi, (_, word: string) => `${stunde(word)}:15`)
      .replace(/\bhalb\s+(\w+)/gi, (_, word: string) => `${davor(stunde(word))}:30`)
      // chrono liest nur Ziffern: „um sieben" bleibt sonst gänzlich unerkannt.
      .replace(
        new RegExp(`\\bum\\s+(${Object.keys(ZAHL).join('|')})\\b`, 'gi'),
        (_, word: string) => `um ${stunde(word)}`,
      )
      // „morgen früh um 8": chrono verschluckt sonst „früh" mitsamt der Uhrzeit.
      .replace(/\bfrüh\s+(um\s)/gi, '$1')
      .replace(/\b(früh|morgens)\b/gi, 'um 8 Uhr')
      .replace(/\bmittags\b/gi, 'um 12 Uhr')
      .replace(/\babends\b/gi, 'um 18 Uhr')
  );
}

/** Was beim Diktieren mitgesprochen wird, aber nicht in den Titel gehört. */
function stripFiller(text: string): string {
  return text
    .replace(/\b(erinnere mich|erinner mich|neue erinnerung|erinnerung)\b:?/gi, ' ')
    .replace(/\ban\s+(die|den|das)\b/gi, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s,.:;-]+|[\s,.:;-]+$/g, '')
    .trim();
}

/** Erster Buchstabe groß — Diktate kommen oft klein zurück. */
function tidy(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Wer um neun Uhr morgens „viertel nach sieben" sagt, meint den Abend — nicht den
 * nächsten Morgen. chrono schiebt eine bereits vergangene Uhrzeit stur auf den
 * Folgetag; hat der Sprecher die Tageshälfte nicht genannt, ist heute Abend die
 * bessere Lesart. Ausdrückliches („7 Uhr morgens", „morgen um sieben") bleibt
 * unangetastet.
 */
function preferThisEvening(result: chrono.ParsedResult, now: Date): Date {
  const when = result.start.date();
  const hour = when.getHours();

  // Jede ausdrückliche Tagesangabe hat Vorrang — „am Freitag", „morgen", „am 3.".
  const namedDay = (['meridiem', 'day', 'weekday', 'month'] as const).some((unit) =>
    result.start.isCertain(unit),
  );
  if (namedDay || hour >= 12) return when;

  // Nur wenn chrono genau auf den Folgetag ausgewichen ist, war es eine reine Uhrzeit.
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (when.toDateString() !== tomorrow.toDateString()) return when;

  const evening = new Date(now);
  evening.setHours(hour + 12, when.getMinutes(), 0, 0);
  return evening > now ? evening : when;
}

export function parseReminder(input: string, now = new Date()): Parsed {
  const withoutRepeat = extractRepeat(input);
  const normalised = normaliseTime(withoutRepeat.text);
  const hit = chrono.de.parse(normalised, now, { forwardDate: true })[0];

  const rest = hit ? normalised.replace(hit.text, ' ') : normalised;
  const title = tidy(stripFiller(rest)) || tidy(stripFiller(input)) || 'Erinnerung';

  return {
    title,
    dueAt: hit ? preferThisEvening(hit, now).getTime() : null,
    repeat: withoutRepeat.repeat,
    matched: hit ? hit.text.trim() : null,
  };
}

// --- Zeitzonen -------------------------------------------------------------
//
// Ein Termin ist eine Wanduhrzeit an einem Ort, kein Moment. „Jeden Montag um 8"
// heißt acht Uhr morgens dort, wo die Erinnerung entstanden ist — auch nach der
// Zeitumstellung und auch, wenn das Telefon gerade in einer anderen Zone steht.
//
// Gerechnet wird deshalb zweistufig: Der gespeicherte Zeitpunkt wird in die
// Wanduhrzeit der Zone übersetzt, dort im Kalender verschoben, und erst danach
// wieder zu einem Zeitpunkt gemacht. Ohne diesen Umweg wandert eine wöchentliche
// Erinnerung über Zeitumstellungen und Reisen hinweg.

export type Wall = { year: number; month: number; day: number; hour: number; minute: number };

/** Die Zone des Geräts — Vorgabe für alles, was ohne eigene Zone gespeichert ist. */
export function deviceZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

const FORMATS = new Map<string, Intl.DateTimeFormat>();

function formatter(zone: string): Intl.DateTimeFormat {
  let existing = FORMATS.get(zone);
  if (!existing) {
    existing = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
    FORMATS.set(zone, existing);
  }
  return existing;
}

/** Wanduhrzeit eines Zeitpunkts in einer Zone. */
export function wallTime(at: number, zone: string): Wall {
  const parts = new Map(
    formatter(zone)
      .formatToParts(new Date(at))
      .map((part) => [part.type, part.value]),
  );
  const read = (name: Intl.DateTimeFormatPartTypes): number => Number(parts.get(name) ?? 0);
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
  };
}

/**
 * Zeitpunkt aus einer Wanduhrzeit. Der Versatz der Zone hängt selbst vom Zeitpunkt
 * ab — deshalb einmal schätzen und einmal nachbessern; das trifft auch die beiden
 * Stunden rund um eine Zeitumstellung.
 */
export function fromWallTime(wall: Wall, zone: string): number {
  const asUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
  const offset = (at: number): number => {
    const there = wallTime(at, zone);
    return Date.UTC(there.year, there.month - 1, there.day, there.hour, there.minute) - at;
  };
  const first = asUtc - offset(asUtc);
  const second = asUtc - offset(first);
  return second;
}

/** Rechnet im Kalender, nicht in Millisekunden: ein Stellvertreter in UTC. */
function proxy(wall: Wall): Date {
  return new Date(Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute));
}

function fromProxy(date: Date): Wall {
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    hour: date.getUTCHours(),
    minute: date.getUTCMinutes(),
  };
}

/**
 * Verschiebt um Monate oder Jahre, ohne über das Monatsende zu rutschen. Der 31.
 * August plus ein Monat ist der 30. September — `setUTCMonth` allein macht daraus den
 * 1. Oktober, weil ein 31. September nicht existiert.
 */
function shiftClamped(date: Date, months: number): void {
  const day = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + months);
  const lastDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
  date.setUTCDate(Math.min(day, lastDay));
}

/**
 * Nächster Termin einer wiederkehrenden Erinnerung, vom bisherigen aus gerechnet.
 *
 * Gerechnet wird in Kalendertagen einer Zeitzone, nicht in Millisekunden. Ohne Zone
 * gilt die des Geräts — dann verhält es sich wie bisher.
 */
export function nextOccurrence(dueAt: number, repeat: Repeat, zone = deviceZone()): number {
  const rule = toRule(repeat);
  const wall = wallTime(dueAt, zone);
  const date = proxy(wall);

  if (rule.unit === 'month' && rule.weekday != null && rule.nth) {
    const target = nthWeekday(wall.year, wall.month - 1 + rule.every, rule.weekday, rule.nth);
    target.setUTCHours(wall.hour, wall.minute, 0, 0);
    return fromWallTime(fromProxy(target), zone);
  }
  if (rule.unit === 'day') date.setUTCDate(date.getUTCDate() + rule.every);
  if (rule.unit === 'week') date.setUTCDate(date.getUTCDate() + 7 * rule.every);
  if (rule.unit === 'month') shiftClamped(date, rule.every);
  if (rule.unit === 'year') shiftClamped(date, 12 * rule.every);
  return fromWallTime(fromProxy(date), zone);
}
